import React, { useState } from 'react';
import Image from 'next/image';
import SwiperCore, { Pagination, Keyboard } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import getBreakpointFlags from '../../../helpers/get-breakpoint-flags';
import PDPImageModal from '../pdp-image-modal/PdpImageModal';
import pdpImageWell from '../../../models/pdp-image-well';
import styles from './pdp-image-well.module.scss';
import useWindowSize from '../../../hooks/useWindowSize';

SwiperCore.use([Pagination, Keyboard]);

type Props = {
  data: pdpImageWell | undefined;
};

const PDPImageWell = ({ data }: Props) => {
  if (!data) return <></>;

  const { width: windowWidth } = useWindowSize();
  const { isDesktop, isXl } = getBreakpointFlags(windowWidth);
  const images =
    isDesktop || isXl ? data?.desktopProductImages : data?.mobileProductImages;

  const [imageIndex, setImageIndex] = useState<number>(0);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const openModal = (i: number) => {
    setImageIndex(i);
    setIsModalOpen(true);
  };

  const desktopPagination = {
    bulletActiveClass: styles['pdp-image-well__pagination-bullet--active'],
    clickable: true,
    renderBullet(index: any, className: string) {
      return data.desktopThumbnailImages
        ? `<span
            class="${`${className} ${styles['pdp-image-well__swiper-pagination__thumbnail-image']}`}"
          >
            <Image
              alt="${data.desktopThumbnailImages[index].alt}"
              class="${
                styles['pdp-image-well__swiper-pagination__thumbnail-image']
              }"
              height="${data.desktopThumbnailImages[index].height}"
              src="${data.desktopThumbnailImages[index].src}"
              width="${data.desktopThumbnailImages[index].width}"
            />
          </span>`
        : '<></>';
    }
  };

  const mobilePagination = {
    bulletActiveClass: styles['pdp-image-well__pagination-bullet--active'],
    clickable: true,
    renderBullet(_: any, className: string) {
      return `<span class=${className}></span>`;
    }
  };

  const renderSwiper = (pagination: any) => {
    return (
      <Swiper
        className={styles['swiper-container']}
        pagination={pagination}
        spaceBetween={0}
        slidesPerView={1}
        keyboard
      >
        {images?.map(({ alt, src, width, height }, i) => (
          <SwiperSlide className={styles['pdp-image-well__slide']} key={alt}>
            <button
              className={styles['pdp-image-well__slide-button']}
              type="button"
              onClick={() => openModal(i)}
              name="pdp-well openModal"
            >
              <div className={styles['pdp-image-well__image-container']}>
                <Image
                  alt={alt}
                  className={styles['pdp-image-well__image']}
                  height={height}
                  src={src}
                  width={width}
                  priority
                />
              </div>
            </button>
          </SwiperSlide>
        ))}
      </Swiper>
    );
  };

  return (
    <div className={styles['pdp-image-well__container']}>
      {(isDesktop || isXl) && renderSwiper(desktopPagination)}
      {!(isDesktop || isXl) && renderSwiper(mobilePagination)}
      <PDPImageModal
        closeModal={closeModal}
        images={images}
        imageIndex={imageIndex}
        isDesktop={isDesktop || isXl}
        isOpen={isModalOpen}
      />
    </div>
  );
};

export default PDPImageWell;
